/**
 * Per-agent completion tag — mirror of functions/helpers/outreachCompletedTag.js.
 * A contact can finish several agents; the label is "{agent name} Completed".
 * The same id is what a later agent uses as its Required-tag starter.
 */

export function completedTagLabel(jobName) {
  const name = String(jobName || '').trim() || 'Agent';
  return `${name} Completed`;
}

export function completedTagId(jobId) {
  const safe = String(jobId || 'unknown').replace(/[.#$[\]/]/g, '_');
  return `outreach_completed_${safe}`;
}

export function isCompletedTagId(tagId) {
  return String(tagId || '').startsWith('outreach_completed_');
}

/** Tags a new agent can start from — every other saved agent. */
export function completedAgentTags(jobs, excludeJobId) {
  return Object.entries(jobs || {})
    .filter(([id]) => id && id !== excludeJobId)
    .map(([id, job]) => ({
      id: completedTagId(id),
      tag: completedTagLabel(job?.name),
      color: '#8b5cf6',
      icon: 'fa-flag-checkered',
      jobId: id,
    }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
}

export function failedTagLabel(jobName) {
  const name = String(jobName || '').trim() || 'Agent';
  return `${name} Failed`;
}

export function failedTagId(jobId) {
  const safe = String(jobId || 'unknown').replace(/[.#$[\]/]/g, '_');
  return `outreach_failed_${safe}`;
}

export function isFailedTagId(tagId) {
  return String(tagId || '').startsWith('outreach_failed_');
}

export function isAgentFailedStatus(value) {
  const v = String(value || '');
  return v === 'Outreach: Failed' || v === 'Outreach: Removed' || / Failed$/.test(v);
}

/** Dropouts a later agent can start from — every other saved agent. */
export function failedAgentTags(jobs, excludeJobId) {
  return Object.entries(jobs || {})
    .filter(([id]) => id && id !== excludeJobId)
    .map(([id, job]) => ({
      id: failedTagId(id),
      tag: failedTagLabel(job?.name),
      color: '#ef4444',
      icon: 'fa-exclamation-triangle',
      jobId: id,
    }))
    .sort((a, b) => a.tag.localeCompare(b.tag));
}

export function labelForPoolTag(tagId, { jobs, tags } = {}) {
  if (!tagId) return '';
  const fromJob = [...completedAgentTags(jobs), ...failedAgentTags(jobs)].find((t) => t.id === tagId);
  if (fromJob) return fromJob.tag;
  const fromTags = (tags || []).find((t) => t.id === tagId);
  if (fromTags) return fromTags.tag || fromTags.name || tagId;
  if (isCompletedTagId(tagId)) return completedTagLabel('');
  if (isFailedTagId(tagId)) return failedTagLabel('');
  return tagId;
}
